import type { MockHistory } from '@/types';

/**
 * Formula C (locked Slot S Q1=C): pass probability composite indicator.
 *
 *   pp = 0.50 * mockComponent + 0.35 * glossComponent + 0.15 * daysFactor
 *
 * Then clipped to [0, 95] (per Slot S spec — never show 100% to avoid overconfidence).
 *
 * Inputs:
 * - mockComponent: best of {mock1, mock2} pct, or 0 if neither taken (cap 95).
 * - glossComponent: % of glossary terms with reps ≥ 2 (i.e., "learned").
 * - daysFactor: clamped score based on time-to-exam:
 *     ≥30 days → 100 (lots of prep time)
 *     14 days → 80
 *     7 days  → 60
 *     0 days  → 40 (last-day cram)
 *     past   → 0
 */

export const COCKPIT_PP_WEIGHTS = {
  mock: 0.5,
  gloss: 0.35,
  days: 0.15,
} as const;

export const PP_CLIP_MAX = 95;

const cap95 = (n: number) => Math.max(0, Math.min(95, n));

const daysFactor = (daysToExam: number | null): number => {
  if (daysToExam === null) return 50; // no exam date set — neutral
  if (daysToExam < 0) return 0;
  if (daysToExam >= 30) return 100;
  if (daysToExam >= 14) return 60 + ((daysToExam - 14) / 16) * 40;
  if (daysToExam >= 7) return 40 + ((daysToExam - 7) / 7) * 20;
  return 40 - ((7 - daysToExam) / 7) * 0; // ≥0 floor of 40 within 0-7 window
};

export function computePassProbability(
  history: MockHistory,
  glossPct: number,
  daysToExam: number | null,
): number {
  const mockBest = Math.max(history.mock1?.pct ?? 0, history.mock2?.pct ?? 0);
  const mockComponent = cap95(mockBest);
  const glossComponent = cap95(glossPct);
  const days = cap95(daysFactor(daysToExam));

  const raw =
    COCKPIT_PP_WEIGHTS.mock * mockComponent +
    COCKPIT_PP_WEIGHTS.gloss * glossComponent +
    COCKPIT_PP_WEIGHTS.days * days;

  return Math.round(Math.max(0, Math.min(PP_CLIP_MAX, raw)));
}

/** Color thresholds per Slot S spec. */
export type PPColor = 'red' | 'orange' | 'green' | 'glow';

export function ppColorClass(pct: number): PPColor {
  if (pct < 50) return 'red';
  if (pct < 65) return 'orange';
  if (pct < 80) return 'green';
  return 'glow';
}

/** Days between today (start-of-day) and exam date. Null if no date. */
export function daysToExam(examDate: string | undefined): number | null {
  if (!examDate) return null;
  const examTs = new Date(examDate + 'T00:00:00').getTime();
  if (Number.isNaN(examTs)) return null;
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  return Math.round((examTs - today) / 86400_000);
}
